/**
 * rebalanceRuns — every rebalance check the owner runs, saved (WORK.md item 45, D20).
 *
 * WHY SAVED. The rebalance lives in the app: nothing runs it on a schedule and
 * nothing emails it. A check that is not written down is a verdict nobody can
 * look back on, so each response the endpoint gives becomes one RUN in the
 * `rebalances` KV key, `{portfolioId: RebalanceRun[]}`, the newest ~90 per
 * portfolio. A run that could not be computed is saved too, WITH the
 * endpoint's reason — a 422 saying "at least two projectable holdings" is the
 * most useful thing the history can hold, and discarding it is how a broken
 * book reads as a quiet one.
 *
 * SAVED WHERE THE REQUEST FINISHES, NOT WHERE IT IS SHOWN. `checkAndRecord`
 * wraps the request itself, so the run is written in the request's own
 * continuation — a check started on the Rebalance tab and finished while the
 * owner is on another tab, or on another page, is saved all the same. A
 * component that watched for the response to land would miss exactly those.
 *
 * WHY THE BADGE READS THIS. The nav dot used to read a per-device localStorage
 * alert that expired after 24 h, so it said nothing on another device and
 * nothing the morning after. It now reads the latest SAVED run of the rule
 * that governs the portfolio — the MODEL check when the portfolio has chosen
 * a model (WORK.md item 57, step 5), else DRIFT when it has a target
 * allocation, else today's OPTIMISER rule (D21) — with no expiry: the label
 * carries the run's date instead, so an old verdict is shown as old rather
 * than hidden. A portfolio with no model reads exactly as before the model
 * check existed: the model rule governs only once `modelSettings.model` is
 * set, and a model run left from a model since cleared is not the rule.
 *
 * THE NIGHTLY RECORD DECIDES (WORK.md item 57 step 6; the owner's word of
 * 2026-11-01, "Nightly record decides"). Once the nightly model batch has
 * written a `model_state` row for a portfolio the model rule governs, the
 * row's verdict is the dot's, whatever the on-demand `model` runs say and
 * however recent they are; an on-demand run shows only in the Model tab. The
 * rows are read once per app load (`loadModelState`). No row, the batch not
 * having run, or a failed or malformed read leaves the dot on the latest
 * on-demand run, as before the batch; a portfolio the drift check or the
 * optimiser governs never reads the rows. The owner declined "newer of the
 * two wins", since a row's `checkedAt` can be a week older than the last
 * night that checked it (modelState.ts), and "one list", the batch appending
 * to `rebalances`, which reverses clash 21.
 *
 * WHAT A RUN CARRIES is what the check stood on — the date, the artefact (its
 * key and fit date), the rule, the verdict and the half that triggered it,
 * gross and net figures, the suggested weights, and the note or reason. Every
 * number was computed by the endpoint or by the CGT chain (ADR-0031 exemption
 * #1); nothing here derives a figure.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { z } from 'zod';
import { toISODate } from '@/core/utils';
import { getSettings } from './portfolioApi';
import { resolveRebalanceTarget } from './rebalanceTarget';
import { resolveModelSettings } from './modelSettings';
import { fetchModelState, type ModelStateRow } from './modelState';
import { createScopedBlobStore } from './scopedBlobStore';
import type { ProjectionDriftResponse, ProjectionModelResponse } from './types';

/** How many runs each portfolio keeps. The oldest beyond this are dropped. */
export const REBALANCE_RUNS_KEEP = 90;

const RunShape = z.object({
  id: z.string(),
  /** ISO timestamp of the response the run records. */
  at: z.string(),
  /** 'YYYY-MM-DD' — the date the label shows. */
  date: z.string(),
  portfolioId: z.string(),
  /** 'optimiser' = today's rule (no target); 'drift' = against the target (D21); 'model' = the model check (item 57). */
  rule: z.enum(['optimiser', 'drift', 'model']),
  /** The model a 'model' run solved, and its version. */
  model: z.object({ id: z.string(), version: z.number() }).optional(),
  /** The projz_ artefact the run rested on; fitDate null when it never answered. */
  artefact: z.object({ key: z.string(), fitDate: z.string().nullable() }),
  verdict: z.enum(['act', 'hold', 'unproven', 'rebalance', 'within_tolerance', 'could_not_run']),
  /** Which half said "rebalance" (drift), or 'return' for an optimiser act. */
  trigger: z.enum(['return', 'risk', 'both']).nullable().optional(),
  gross: z.number().nullable(),
  net: z.number().nullable(),
  probability: z.number().nullable().optional(),
  volGap: z.number().nullable().optional(),
  trackingError: z.number().nullable().optional(),
  /** The suggested (or target) book the run judged, ticker → weight. */
  weights: z.record(z.string(), z.number()).optional(),
  note: z.string().optional(),
  /** Why it could not run — the endpoint's own words, never dropped. */
  reason: z.string().optional(),
});
export const RebalanceRunSchema = RunShape.loose();
export type RebalanceRun = z.infer<typeof RebalanceRunSchema>;
export type RebalanceRule = RebalanceRun['rule'];
/** A run without its stamps — what a judge produces from a response. */
export type RunFields = Omit<z.infer<typeof RunShape>, 'id' | 'at' | 'date'>;

const RULES: readonly RebalanceRule[] = ['optimiser', 'drift', 'model'];

const store = createScopedBlobStore<RebalanceRun>('rebalances', RebalanceRunSchema);

/** Newest first by `at`, bounded to REBALANCE_RUNS_KEEP. Pure. */
export function upsertRun(slice: RebalanceRun[], run: RebalanceRun, keep = REBALANCE_RUNS_KEEP): RebalanceRun[] {
  const rest = slice.filter((r) => r.id !== run.id);
  return [run, ...rest].sort((a, b) => b.at.localeCompare(a.at)).slice(0, keep);
}

/** The newest run in a slice — of one rule when given — or null. Pure. */
export function latestOf(slice: RebalanceRun[], rule?: RebalanceRule): RebalanceRun | null {
  let best: RebalanceRun | null = null;
  for (const r of slice) {
    if (rule && r.rule !== rule) continue;
    if (!best || r.at > best.at) best = r;
  }
  return best;
}

/**
 * Is this run, or nightly row, worth summoning the owner for? `act` (today's
 * rule cleared both halves) and `rebalance` (a drift half tripped, or a model
 * check's book beat today's). Nothing else badges: `hold` and
 * `within_tolerance` are the common case, `unproven` is a reason to look when
 * already there, a check that could not compute has no verdict to act on, and
 * a nightly `no_model` row has no model to judge.
 */
export function isActionable(run: { verdict: string } | null | undefined): boolean {
  return run?.verdict === 'act' || run?.verdict === 'rebalance';
}

// ── the in-memory view the badge subscribes to ──────────────────────────────
// Per portfolio, per rule: undefined = not loaded yet; null = loaded, none saved.
type Latest = Partial<Record<RebalanceRule, RebalanceRun | null>>;
const latest = new Map<string, Latest>();
/** Does the portfolio have a target allocation? Absent = not known yet. */
const targetSet = new Map<string, boolean>();
/** Has the portfolio chosen a model (item 57)? Absent reads as no model. */
const modelSet = new Map<string, boolean>();
/** The last save's failure per portfolio, or null once a save succeeds. */
const saveErrors = new Map<string, string | null>();
/**
 * The nightly rows by portfolio (item 57 step 6), read once per app load:
 * undefined until the first read settles, null after a failed one.
 */
let modelRows: ReadonlyMap<string, ModelStateRow> | null | undefined;
let modelRead: Promise<void> | null = null;
/** Moved on by the test reset, so a read still out then lands nowhere. */
let modelGen = 0;
const listeners = new Set<() => void>();

function notify(): void {
  for (const fn of listeners) {
    try { fn(); } catch { /* one bad listener must not stop the rest */ }
  }
}

export function subscribeRebalanceRuns(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** The cached latest run of one rule: undefined until loaded. */
export function getLatestRun(portfolioId: string, rule: RebalanceRule): RebalanceRun | null | undefined {
  return latest.get(portfolioId)?.[rule];
}

/**
 * The rule that governs the portfolio, or undefined until known: the model
 * check once a model is chosen (item 57), else drift with a target and the
 * optimiser without one (D21). A model not yet known to be chosen is read as
 * none, so a portfolio without one keeps today's rule from the first read.
 */
export function governingRule(portfolioId: string): RebalanceRule | undefined {
  if (modelSet.get(portfolioId) === true) return 'model';
  const t = targetSet.get(portfolioId);
  return t === undefined ? undefined : t ? 'drift' : 'optimiser';
}

/**
 * The run the badge reads: the latest of the GOVERNING rule. With a target,
 * a later optimiser "hold" says nothing about drift and must not clear a
 * drift "rebalance"; without one, a drift run left from a cleared target is
 * not the rule any more. Undefined until both the rule and its run are known.
 */
export function getBadgeRun(portfolioId: string): RebalanceRun | null | undefined {
  const rule = governingRule(portfolioId);
  return rule === undefined ? undefined : getLatestRun(portfolioId, rule);
}

/**
 * The portfolio's nightly row: undefined until the read settles, null when
 * the read holds none for it or failed.
 */
export function getModelRow(portfolioId: string): ModelStateRow | null | undefined {
  return modelRows === undefined ? undefined : (modelRows?.get(portfolioId) ?? null);
}

/**
 * The nightly row that decides the dot ("Nightly record decides"): the
 * portfolio's row when the model rule governs it, else null, since drift and
 * the optimiser never read the rows. Undefined while a model portfolio's
 * first read is out, so the dot waits for it rather than showing an
 * on-demand verdict the row may overrule.
 */
export function getBadgeRow(portfolioId: string): ModelStateRow | null | undefined {
  return governingRule(portfolioId) === 'model' ? getModelRow(portfolioId) : null;
}

/**
 * Tell the cache whether the portfolio has a target — from the page that
 * already holds settings, or from the settings card after a save — so the
 * badge switches rule at once rather than on the next load.
 */
export function setRebalanceTargetKnown(portfolioId: string, hasTarget: boolean): void {
  if (!portfolioId || targetSet.get(portfolioId) === hasTarget) return;
  targetSet.set(portfolioId, hasTarget);
  notify();
}

/** Tell the cache whether the portfolio has chosen a model, as the target above. */
export function setModelChosenKnown(portfolioId: string, chosen: boolean): void {
  if (!portfolioId || modelSet.get(portfolioId) === chosen) return;
  modelSet.set(portfolioId, chosen);
  notify();
}

/** Read the saved runs for a portfolio, newest first. Public read. */
export async function listRebalanceRuns(portfolioId: string): Promise<RebalanceRun[]> {
  const slice = await store.get(portfolioId);
  return [...slice].sort((a, b) => b.at.localeCompare(a.at));
}

/**
 * Load the latest saved run of each rule, and whether the portfolio has a
 * target, into the cache the badge reads. A failed read leaves the cache
 * UNLOADED rather than recording "no run" — a network error is not evidence
 * that nothing was saved.
 *
 * NEVER OLDER. A run recorded in this tab while the read was in flight is
 * newer than anything the read can return — including an EMPTY read, which
 * can arrive after this tab showed a run whose save has not landed yet. So a
 * loaded run replaces the cached one only when it is at least as new, and a
 * loaded "none" only fills a slot that was never loaded.
 */
export async function loadLatestRun(portfolioId: string): Promise<RebalanceRun | null | undefined> {
  if (!portfolioId) return undefined;
  try {
    const [slice, settings] = await Promise.all([store.get(portfolioId), getSettings()]);
    const entry: Latest = { ...(latest.get(portfolioId) ?? {}) };
    for (const rule of RULES) {
      const run = latestOf(slice, rule);
      const cur = entry[rule];
      if (run === null) {
        if (cur === undefined) entry[rule] = null;
      } else if (!cur || run.at >= cur.at) {
        entry[rule] = run;
      }
    }
    latest.set(portfolioId, entry);
    if (!targetSet.has(portfolioId)) {
      targetSet.set(portfolioId, resolveRebalanceTarget(settings, portfolioId) != null);
    }
    if (!modelSet.has(portfolioId)) {
      modelSet.set(portfolioId, resolveModelSettings(settings, portfolioId).settings.model != null);
    }
    notify();
    return getBadgeRun(portfolioId);
  } catch {
    return undefined;
  }
}

/**
 * Read the nightly rows into the cache, once per app load, and notify. "The
 * batch has not run" is an answer, and holds no row. A failed or malformed
 * read holds none either, so the dot stays on the on-demand runs, and the
 * next caller reads again, as a failed `loadLatestRun` is loaded again.
 */
export function loadModelState(fetcher: typeof fetch = fetch): Promise<void> {
  if (modelRows) return Promise.resolve();
  if (modelRead) return modelRead;
  const gen = modelGen;
  const read = fetchModelState(fetcher).then(
    (r) => { if (gen === modelGen) modelRows = new Map(r.rows.map((row) => [row.portfolioId, row])); },
    () => { if (gen === modelGen) modelRows = null; },
  ).then(() => {
    if (gen !== modelGen) return;
    modelRead = null;
    notify();
  });
  modelRead = read;
  return read;
}

/** The portfolio's nightly row, live, for the Model tab: null when there is none or the read failed. */
export function useModelRow(portfolioId: string): ModelStateRow | null {
  useEffect(() => { void loadModelState(); }, []);
  return useSyncExternalStore(subscribeRebalanceRuns, () => getModelRow(portfolioId) ?? null, () => null);
}

/**
 * Make a run the latest of its rule in this tab (the badge updates at once)
 * without writing it. `recordRebalanceRun` persists.
 */
export function showRun(run: RebalanceRun): void {
  const entry: Latest = { ...(latest.get(run.portfolioId) ?? {}) };
  const cur = entry[run.rule];
  if (cur && cur.id !== run.id && cur.at > run.at) return;
  entry[run.rule] = run;
  latest.set(run.portfolioId, entry);
  notify();
}

/**
 * Save a run: upsert by id into the FRESH stored slice and keep the newest
 * REBALANCE_RUNS_KEEP. Serialized per key with every other writer of it.
 * Rejects on a failed write; the failure is also kept per portfolio
 * (`useRebalanceSaveError`), so a panel mounted later can still say the run
 * was not saved.
 */
export async function recordRebalanceRun(run: RebalanceRun): Promise<RebalanceRun[]> {
  const parsed = RebalanceRunSchema.parse(run);
  showRun(parsed);
  try {
    const next = await store.update((slice) => upsertRun(slice, parsed), parsed.portfolioId);
    setSaveError(parsed.portfolioId, null);
    return next;
  } catch (e) {
    setSaveError(parsed.portfolioId, e instanceof Error ? e.message : 'save failed');
    throw e;
  }
}

function setSaveError(portfolioId: string, message: string | null): void {
  if ((saveErrors.get(portfolioId) ?? null) === message) return;
  saveErrors.set(portfolioId, message);
  notify();
}

export function getRebalanceSaveError(portfolioId: string): string | null {
  return saveErrors.get(portfolioId) ?? null;
}

/** The last save failure for a portfolio, live. */
export function useRebalanceSaveError(portfolioId: string): string | null {
  return useSyncExternalStore(
    subscribeRebalanceRuns,
    () => getRebalanceSaveError(portfolioId),
    () => null,
  );
}

/** A run id: time-ordered and unique enough for one owner's history. */
export function newRunId(now: Date = new Date()): string {
  return `${now.getTime().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

// ── saving a check where it finishes ────────────────────────────────────────
/** Response object → the stamps of the run that recorded it. */
const stampByResponse = new WeakMap<object, Pick<RebalanceRun, 'id' | 'at' | 'date'>>();

/** The id and time of the run a response was saved as, if it was. */
export function runStampFor(response: object): Pick<RebalanceRun, 'id' | 'at' | 'date'> | undefined {
  return stampByResponse.get(response);
}

function stamp(now: Date): Pick<RebalanceRun, 'id' | 'at' | 'date'> {
  return { id: newRunId(now), at: now.toISOString(), date: toISODate(now) };
}

/**
 * Run a rebalance request and save its outcome as a run, in the request's own
 * continuation — so the run is saved whether or not anything that started it
 * is still mounted when it finishes.
 *
 *   - a response → `judge(response)` → a run (none when the judge returns null);
 *   - a failure  → a `could_not_run` run carrying the error text, which for a
 *     422 is the endpoint's own reason; then the failure is rethrown, so the
 *     caller shows it exactly as before.
 *
 * The save is not awaited: the answer is shown at once, and a failed save is
 * reported through `useRebalanceSaveError` rather than swallowed.
 */
export async function checkAndRecord<T extends object>(
  { portfolioId, rule }: { portfolioId: string; rule: RebalanceRule },
  task: () => Promise<T>,
  judge: (response: T) => RunFields | null,
): Promise<T> {
  let response: T;
  try {
    response = await task();
  } catch (e) {
    void recordRebalanceRun({
      ...stamp(new Date()), portfolioId, rule,
      artefact: { key: `projz_${portfolioId}`, fitDate: null },
      verdict: 'could_not_run', trigger: null, gross: null, net: null,
      reason: e instanceof Error ? e.message : 'Unknown error',
    }).catch(() => {});
    throw e;
  }
  const s = stamp(new Date());
  stampByResponse.set(response, s);
  const fields = judge(response);
  if (fields) void recordRebalanceRun({ ...fields, ...s }).catch(() => {});
  return response;
}

/**
 * A /projection-drift response as a run. A copy of the endpoint's answer —
 * the verdict, the half and every figure are Python's (ADR-0031).
 */
export function driftRunFields(r: ProjectionDriftResponse): RunFields {
  return {
    portfolioId: r.portfolio_id,
    rule: 'drift',
    artefact: { key: `projz_${r.portfolio_id}`, fitDate: r.fit_date },
    verdict: r.verdict,
    trigger: r.trigger,
    gross: r.return_gap_gross,
    net: r.return_gap_net,
    probability: r.return_gap_prob,
    volGap: r.vol_gap,
    trackingError: r.tracking_error,
    weights: r.target_weights,
    note: r.note,
  };
}

/**
 * A /projection-model response as a run (item 57, step 5). Every figure is
 * Python's: the books' net returns are after each fund's ongoing charge, the
 * trade costs, the sale tax the app's curves priced and the fixed dealing
 * charges spread over the horizon. The verdict is one comparison of those
 * figures with the owner's net-gain bar (`triggers.minNetGain`), as the
 * optimiser rule's is (`rebalanceVerdict`): `rebalance` when the answer is a
 * new book (not today's, with turnover) whose net return beats today's by
 * more than the bar, else `within_tolerance`. D21's confidence half, the vol
 * gap and the cooldown are the nightly check's (step 6), not this one's.
 */
export function modelRunFields(
  r: ProjectionModelResponse, model: { id: string; version: number }, minNetGain: number,
): RunFields {
  const net = r.suggested.net_return - r.current.net_return;
  const moved = r.source !== 'current' && r.suggested.turnover > 0;
  const act = moved && net > minNetGain;
  return {
    portfolioId: r.portfolio_id,
    rule: 'model',
    model: { id: model.id, version: model.version },
    artefact: { key: `projz_${r.portfolio_id}`, fitDate: r.fit_date },
    verdict: act ? 'rebalance' : 'within_tolerance',
    trigger: act ? 'return' : null,
    gross: r.suggested.gross_return - r.current.gross_return,
    net,
    volGap: r.suggested.vol - r.current.vol,
    weights: r.suggested_weights ?? Object.fromEntries(r.rows.map((x) => [x.ticker, x.suggested_weight])),
    note: r.note,
  };
}

/** Test seam — forget every cached run, target, nightly row and save error, and notify. */
export function _resetRebalanceRunsCache(): void {
  latest.clear();
  targetSet.clear();
  modelSet.clear();
  saveErrors.clear();
  modelRows = undefined;
  modelRead = null;
  modelGen += 1;
  notify();
}
